// Landing.jsx
import { GoogleLogin } from '@react-oauth/google';
import React, { useState } from 'react';
import {
  IonContent,
  IonPage,
  IonImg,
  IonButton,
  IonToast,
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonText,
  IonGrid,
  IonRow,
  IonCol,
  IonInput,
  IonItem,
  IonLabel,
  IonSelect,
  IonSelectOption,
  IonIcon
} from '@ionic/react';
import { callOutline } from 'ionicons/icons';
import companyLogo from '../../assets/logo-pure-white34.png';
import landingImage from '../../assets/land_image_bw.png';
import termsText from '../../assets/terms.txt';
import './LandingScreen.css';
import { jwtDecode } from "jwt-decode";
import axios from 'axios'; // Import Axios
import { API_URL } from '../../index';
import LoadingScreen from '../LoadingScreen/LoadingScreen';

const Landing = () => {
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastColor, setToastColor] = useState("danger");
  const [phoneModalOpen, setPhoneModalOpen] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [selectedCountryCode, setSelectedCountryCode] = useState("");
  const [passcodeModalOpen, setPasscodeModalOpen] = useState(false);
  const [passcode, setPasscode] = useState("");
  const [registrationModalOpen, setRegistrationModalOpen] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [pendingAuthMethod, setPendingAuthMethod] = useState(null); // 'google' or 'phone'
  const [termsContent, setTermsContent] = useState("");
  const [googleCredentialPending, setGoogleCredentialPending] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [phoneSubmitted, setPhoneSubmitted] = useState(false);
  const [passcodeVerified, setPasscodeVerified] = useState(false);
  
  const closePhoneModal = () => setPhoneModalOpen(false);
  const closePasscodeModal = () => setPasscodeModalOpen(false);
  const closeRegistrationModal = () => setRegistrationModalOpen(false);
  const closeTermsModal = () => {
    setTermsModalOpen(false);
  };

  // Load terms and conditions content
  React.useEffect(() => {
    fetch(termsText)
      .then(response => response.text())
      .then(text => setTermsContent(text))
      .catch(error => console.error('Error loading terms:', error));
  }, []);


  // Common country codes
  const countryCodes = [
    { code: "1", country: "US/Canada" },
    { code: "44", country: "UK" },
    { code: "91", country: "India" },
    { code: "86", country: "China" },
    { code: "81", country: "Japan" },
    { code: "49", country: "Germany" },
    { code: "33", country: "France" },
    { code: "39", country: "Italy" },
    { code: "34", country: "Spain" },
    { code: "31", country: "Netherlands" },
    { code: "61", country: "Australia" },
    { code: "64", country: "New Zealand" },
    { code: "27", country: "South Africa" },
    { code: "55", country: "Brazil" },
    { code: "52", country: "Mexico" },
    { code: "57", country: "Colombia" },
    { code: "54", country: "Argentina" },
    { code: "56", country: "Chile" },
    { code: "51", country: "Peru" },
    { code: "593", country: "Ecuador" }
  ];

  /**
   * Handles Google authentication response after terms are accepted
   * @param {Object} googleResponse - The response from Google OAuth
   */
  const responseMessage = (googleResponse) => {
    console.log(googleResponse);
    const credentialId = googleResponse.credential;
    jwtDecode(credentialId);
    setIsLoading(true);

    // send response to localhost:4000/login with post request
    axios.post(`${API_URL}/login`, googleResponse)
      .then(response => {
        console.log(response.data)

        const dataJSON = JSON.stringify(response.data);
        localStorage.setItem('user', dataJSON);
        console.log('Data added to local storage');
        window.location.reload();
      })
      .catch(error => {
        console.error('Google login error:', error);
        console.error('Error details:', error.message);
        console.error('Error response:', error.response?.data);
        
        let errorMessage = 'Connection to server failed. Please try again later.';
        if (error.message === 'Network Error' || !error.response) {
          errorMessage = 'Cannot connect to server. Please check your internet connection.';
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        }
        
        setToastMessage(errorMessage);
        setShowToast(true);
        setIsLoading(false);
      });
  };

  const errorMessage = (error) => {
    console.log(error);
    setToastMessage('Login failed. Please try again.');
    setShowToast(true);
  };

  /**
   * Intercepts Google login to show terms first
   * @param {Object} googleResponse - The response from Google OAuth
   */
  const handleGoogleLoginAttempt = (googleResponse) => {
    // Store the Google credential and show terms modal
    setGoogleCredentialPending(googleResponse);
    setPendingAuthMethod('google');
    setTermsModalOpen(true);
  };

  /**
   * Intercepts phone login to show terms first
   */
  const handlePhoneLoginAttempt = () => {
    setPendingAuthMethod('phone');
    setTermsModalOpen(true);
  };

  /**
   * Handles terms acceptance and proceeds with authentication
   */
  const handleAcceptTerms = () => {
    setTermsAccepted(true);
    closeTermsModal();
  };

  /**
   * Called when terms modal is fully dismissed
   * This ensures no focus conflicts between modals
   */
  const handleTermsModalDismissed = () => {
    if (termsAccepted) {
      if (pendingAuthMethod === 'google' && googleCredentialPending) {
        // Proceed with Google authentication
        responseMessage(googleCredentialPending);
        // Clean up after Google auth
        setPendingAuthMethod(null);
        setGoogleCredentialPending(null);
      } else if (pendingAuthMethod === 'phone') {
        // Open phone modal now that terms modal is fully closed
        setPhoneModalOpen(true);
        // Don't clean up pendingAuthMethod yet - we still need it for the phone flow
      }
      // Reset terms accepted flag
      setTermsAccepted(false);
    } else {
      // User declined or closed modal - clean up
      setPendingAuthMethod(null);
      setGoogleCredentialPending(null);
    }
  };

  /**
   * Called when phone modal is fully dismissed
   */
  const handlePhoneModalDismissed = () => {
    if (phoneSubmitted) {
      setPasscodeModalOpen(true);
      setPhoneSubmitted(false);
    } else {
      // User cancelled - clean up
      setPendingAuthMethod(null);
    }
  };

  /**
   * Called when passcode modal is fully dismissed
   */
  const handlePasscodeModalDismissed = () => {
    if (passcodeVerified) {
      setRegistrationModalOpen(true);
      setPasscodeVerified(false);
    }
    // Clean up regardless - flow is complete or cancelled
    setPendingAuthMethod(null);
  };

  /**
   * Handles phone login process
   */
  const handlePhoneLogin = () => {
    if (!selectedCountryCode) {
      setToastMessage('Please select a country code');
      setToastColor('danger');
      setShowToast(true);
      return;
    }

    if (phoneNumber.trim()) {
      // Validate phone number format
      const phoneRegex = /^\d{10}$/;
      if (!phoneRegex.test(phoneNumber)) {
        setToastMessage('Please enter a valid 10-digit phone number');
        setToastColor('danger');
        setShowToast(true);
        return;
      }

      // Combine country code and phone number
      const fullPhoneNumber = `+${selectedCountryCode}${phoneNumber}`;
      console.log('Full phone number with country code:', fullPhoneNumber);
      setIsLoading(true);

      // Send phone number to /loginphone endpoint
      axios.post(`${API_URL}/loginphone`, { phoneNumber: fullPhoneNumber })
        .then(response => {
          console.log('Phone login response:', response.data);
          setToastMessage('Please check your phone for the passcode');
          setToastColor('success');
          setShowToast(true);
          setIsLoading(false);
          setPhoneSubmitted(true);
          setPhoneModalOpen(false);

          // Handle successful login - you can add your logic here
          // For example, store user data, redirect, etc.
        })
        .catch(error => {
          console.error('Phone login error:', error);
          console.error('Error details:', error.message);
          console.error('Error response:', error.response?.data);
          console.error('API URL:', `${API_URL}/loginphone`);
          
          let errorMessage = 'Phone authentication failed. Please try again.';
          if (error.message === 'Network Error' || !error.response) {
            errorMessage = 'Cannot connect to server. Please check your internet connection.';
          } else if (error.response?.data?.message) {
            errorMessage = error.response.data.message;
          }
          
          setToastMessage(errorMessage);
          setToastColor('danger');
          setShowToast(true);
          setIsLoading(false);
        });
    } else {
      setToastMessage('Please enter a valid phone number');
      setToastColor('danger');
      setShowToast(true);
    }
  };

  const handlePasscodeVerification = () => {
    if (passcode.trim()) {
      // Validate passcode format (6 digits)
      const passcodeRegex = /^\d{6}$/;
      if (!passcodeRegex.test(passcode)) {
        setToastMessage('Please enter a valid 6-digit passcode');
        setToastColor('danger');
        setShowToast(true);
        return;
      }

      // Combine country code and phone number for the verification call
      const fullPhoneNumber = `+${selectedCountryCode}${phoneNumber}`;
      setIsLoading(true);

      // Send passcode to /loginphonecheck endpoint
      axios.post(`${API_URL}/loginphonecheck`, {
        phoneNumber: fullPhoneNumber,
        passcode: passcode
      })
        .then(response => {
          console.log('Passcode verification response:', response.data);

          if (response.data.status === 'user_not_found') {
            // New user - show registration modal
            setToastMessage('Please complete your registration');
            setToastColor('success');
            setShowToast(true);
            setIsLoading(false);
            setPasscodeVerified(true);
            setPasscodeModalOpen(false);
          } else if (response.data.status === 'user_exists') {
            // Existing user - authentication complete
            setToastMessage('Phone authentication successful!');
            setToastColor('success');
            setShowToast(true);
            setPasscodeModalOpen(false);
            setIsLoading(false);

            // Store user data in localStorage if available
            if (response.data.userData) {
              const userDataJSON = JSON.stringify(response.data.userData);
              localStorage.setItem('user', userDataJSON);
              console.log('User data stored in localStorage:', response.data.userData);
            }

            // Clear all form data
            setPhoneNumber("");
            setSelectedCountryCode("");
            setPasscode("");

            // Redirect to home page after successful authentication
            setTimeout(() => {
              window.location.reload();
            }, 1500);
          } else {
            // Handle other statuses or unexpected responses
            setToastMessage('Authentication completed');
            setToastColor('success');
            setShowToast(true);
            setPasscodeModalOpen(false);
            setIsLoading(false);

            // Store user data in localStorage if available
            if (response.data.userData) {
              const userDataJSON = JSON.stringify(response.data.userData);
              localStorage.setItem('user', userDataJSON);
              console.log('User data stored in localStorage:', response.data.userData);
            }

            // Clear all form data
            setPhoneNumber("");
            setSelectedCountryCode("");
            setPasscode("");

            // Redirect to home page after successful authentication
            setTimeout(() => {
              window.location.reload();
            }, 1500);
          }

          // Handle successful verification - you can add your logic here
          // For example, store user data, redirect, etc.
        })
        .catch(error => {
          console.error('Passcode verification error:', error);
          console.error('Error details:', error.message);
          console.error('Error response:', error.response?.data);
          
          let errorMessage = 'Passcode verification failed. Please try again.';
          if (error.message === 'Network Error' || !error.response) {
            errorMessage = 'Cannot connect to server. Please check your internet connection.';
          } else if (error.response?.data?.message) {
            errorMessage = error.response.data.message;
          }
          
          setToastMessage(errorMessage);
          setToastColor('danger');
          setShowToast(true);
          setIsLoading(false);
        });
    } else {
      setToastMessage('Please enter a valid passcode');
      setToastColor('danger');
      setShowToast(true);
    }
  };

  const handleRegistration = () => {
    // Validate First Name (letters only, 2-50 characters)
    const firstNameRegex = /^[a-zA-Z]{2,50}$/;
    if (!firstNameRegex.test(firstName.trim())) {
      setToastMessage('First name must be 2-50 letters only');
      setToastColor('danger');
      setShowToast(true);
      return;
    }

    // Validate Last Name (letters only, 2-50 characters)
    const lastNameRegex = /^[a-zA-Z]{2,50}$/;
    if (!lastNameRegex.test(lastName.trim())) {
      setToastMessage('Last name must be 2-50 letters only');
      setToastColor('danger');
      setShowToast(true);
      return;
    }

    // Validate Email (standard email format)
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(email.trim())) {
      setToastMessage('Please enter a valid email address');
      setToastColor('danger');
      setShowToast(true);
      return;
    }

    // All validation passed - send registration data to /loginphoneregister
    const fullPhoneNumber = `+${selectedCountryCode}${phoneNumber}`;
    const registrationData = {
      phoneNumber: fullPhoneNumber,
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim()
    };

    console.log('Sending registration data:', registrationData);
    setIsLoading(true);

    axios.post(`${API_URL}/loginphoneregister`, registrationData)
      .then(response => {
        console.log('Registration response:', response.data);
        
        // Check if registration was successful
        if (response.data.success) {
          // Store user data in localStorage
          const userDataJSON = JSON.stringify(response.data.user);
          localStorage.setItem('user', userDataJSON);
          console.log('User data stored in localStorage:', response.data.user);
          
          // Show appropriate success message based on backend response
          const successMessage = response.data.message || 'Registration completed successfully!';
          setToastMessage(successMessage);
          setToastColor('success');
          setShowToast(true);
          setRegistrationModalOpen(false);
          setIsLoading(false);

          // Clear all form data
          setPhoneNumber("");
          setSelectedCountryCode("");
          setPasscode("");
          setFirstName("");
          setLastName("");
          setEmail("");

          // Redirect to home page after successful registration
          setTimeout(() => {
            window.location.reload();
          }, 1500);
        } else {
          // Handle case where backend returns success: false
          setToastMessage(response.data.message || 'Registration failed. Please try again.');
          setToastColor('danger');
          setShowToast(true);
          setIsLoading(false);
        }
      })
      .catch(error => {
        console.error('Registration error:', error);
        
        // Handle different types of errors
        if (error.response && error.response.data && error.response.data.message) {
          // Backend returned a specific error message
          setToastMessage(error.response.data.message);
        } else if (error.response && error.response.status === 400) {
          // Bad request - validation error
          setToastMessage('Please check your information and try again.');
        } else if (error.response && error.response.status === 500) {
          // Server error
          setToastMessage('Server error. Please try again later.');
        } else {
          // Network or other error
          setToastMessage('Registration failed. Please check your connection and try again.');
        }
        
        setToastColor('danger');
        setShowToast(true);
        setIsLoading(false);
      });
  };

  return (
    <IonPage className="landing-page">
      <IonContent fullscreen className="landing-content">
        {/* Hero Section */}
        <div className="hero-section">
          <div className="hero-background">
            <div className="geometric-shapes">
              <div className="shape shape-1"></div>
              <div className="shape shape-2"></div>
              <div className="shape shape-3"></div>
            </div>
          </div>
          
          <div className="hero-content">
            {/* Logo */}
            <div className="logo-container">
              <IonImg src={companyLogo} alt="HOOD Logo" className="landing-logo" />
            </div>

            {/* Main Content */}
            <div className="main-content">
              <div className="text-content">
                <h1 className="hero-title">
                  Start your trip
                </h1>
                <p className="hero-subtitle">
                  Your journey starts here. Connect, travel, and discover with our community.
                </p>
              </div>

              {/* Illustration */}
              <div className="illustration-container">
                <IonImg src={landingImage} alt="Illustration" className="landing-illustration" />
              </div>
            </div>

            {/* Authentication Section */}
            <div className="auth-section">
              <div className="auth-container">
                <h2 className="auth-title">Get Started</h2>
                
                {/* Google Sign-in */}
                <div className="google-signin-container">
                  <GoogleLogin
                    type="standard"
                    theme="filled_black"
                    text="continue_with"
                    size="large"
                    shape="pill"
                    logo_alignment="left"
                    cookiePolicy="single_host_origin"
                    onSuccess={handleGoogleLoginAttempt}
                    onError={errorMessage}
                  />
                </div>

                {/* Divider */}
                <div className="divider">
                  <span className="divider-text">or</span>
                </div>

                {/* Phone Sign-in */}
                <IonButton
                  expand="block"
                  className="phone-signin-button"
                  onClick={handlePhoneLoginAttempt}
                >
                  <IonIcon icon={callOutline} slot="start" />
                  CONTINUE WITH PHONE
                </IonButton>
              </div>
            </div>
          </div>
        </div>


        {/* Phone Number Modal */}
        <IonModal isOpen={phoneModalOpen} onDidDismiss={handlePhoneModalDismissed} className="phone-modal">
          <IonHeader className="phone-modal-header">
            <IonToolbar color="dark">
              <IonTitle>Sign in with Phone Number</IonTitle>
            </IonToolbar>
          </IonHeader>
          <IonContent className="phone-modal-content" scrollY={true}>
            <IonGrid>
              <IonRow>
                <IonCol size="12" className="ion-text-center">
                  <IonItem className="phone-input-item">
                    <IonLabel position="stacked" color="dark">Country Code</IonLabel>
                    <IonSelect
                      placeholder="Select Country Code"
                      value={selectedCountryCode}
                      onIonChange={(e) => setSelectedCountryCode(e.detail.value)}
                      className="phone-select"
                      interface="popover"
                    >
                      {countryCodes.map((code) => (
                        <IonSelectOption key={code.code} value={code.code}>
                          {code.country} ({code.code})
                        </IonSelectOption>
                      ))}
                    </IonSelect>
                  </IonItem>
                </IonCol>
              </IonRow>
              <IonRow>
                <IonCol size="12" className="ion-text-center">
                  <IonItem className="phone-input-item">
                    <IonLabel position="stacked" color="dark">Phone Number</IonLabel>
                    <IonInput
                      type="tel"
                      placeholder="Enter your phone number"
                      value={phoneNumber}
                      onIonInput={(e) => setPhoneNumber(e.detail.value)}
                      className="phone-input"
                    />
                  </IonItem>
                </IonCol>
              </IonRow>
              <IonRow>
                <IonCol size="12">
                  <IonButton
                    expand="block"
                    color="dark"
                    onClick={handlePhoneLogin}
                    className="phone-modal-btn"
                  >
                    Continue
                  </IonButton>
                </IonCol>
              </IonRow>
              <IonRow>
                <IonCol size="12">
                  <IonButton
                    expand="block"
                    fill="outline"
                    color="medium"
                    onClick={closePhoneModal}
                    className="phone-modal-btn"
                  >
                    Cancel
                  </IonButton>
                </IonCol>
              </IonRow>
            </IonGrid>
          </IonContent>
        </IonModal>

        {/* Passcode Modal */}
        <IonModal isOpen={passcodeModalOpen} onDidDismiss={handlePasscodeModalDismissed} className="phone-modal">
          <IonHeader className="phone-modal-header">
            <IonToolbar color="dark">
              <IonTitle>Enter Passcode</IonTitle>
            </IonToolbar>
          </IonHeader>
          <IonContent className="phone-modal-content" scrollY={true}>
            <IonGrid>
              <IonRow>
                <IonCol size="12" className="ion-text-center">
                  <IonText color="dark">
                    <p>Please enter the 6-digit passcode sent to your phone</p>
                  </IonText>
                </IonCol>
              </IonRow>
              <IonRow>
                <IonCol size="12" className="ion-text-center">
                  <IonItem className="phone-input-item">
                    <IonLabel position="stacked" color="dark">6-Digit Passcode</IonLabel>
                    <IonInput
                      type="number"
                      placeholder="Enter 6-digit passcode"
                      value={passcode}
                      onIonInput={(e) => setPasscode(e.detail.value)}
                      className="phone-input"
                      maxlength={6}
                    />
                  </IonItem>
                </IonCol>
              </IonRow>
              <IonRow>
                <IonCol size="12">
                  <IonButton
                    expand="block"
                    color="dark"
                    onClick={handlePasscodeVerification}
                    className="phone-modal-btn"
                  >
                    Continue
                  </IonButton>
                </IonCol>
              </IonRow>
              <IonRow>
                <IonCol size="12">
                  <IonButton
                    expand="block"
                    fill="outline"
                    color="medium"
                    onClick={closePasscodeModal}
                    className="phone-modal-btn"
                  >
                    Cancel
                  </IonButton>
                </IonCol>
              </IonRow>
            </IonGrid>
          </IonContent>
        </IonModal>

        {/* Registration Modal */}
        <IonModal isOpen={registrationModalOpen} onDidDismiss={closeRegistrationModal} className="phone-modal">
          <IonHeader className="phone-modal-header">
            <IonToolbar color="dark">
              <IonTitle>Complete Registration</IonTitle>
            </IonToolbar>
          </IonHeader>
          <IonContent className="phone-modal-content" scrollY={true}>
            <IonGrid>
              <IonRow>
                <IonCol size="12" className="ion-text-center">
                  <IonText color="dark">
                    <p>Please provide your information to complete registration</p>
                  </IonText>
                </IonCol>
              </IonRow>
              <IonRow>
                <IonCol size="12" className="ion-text-center">
                  <IonItem className="phone-input-item">
                    <IonLabel position="stacked" color="dark">First Name</IonLabel>
                    <IonInput
                      type="text"
                      placeholder="Enter your first name"
                      value={firstName}
                      onIonInput={(e) => setFirstName(e.detail.value)}
                      className="phone-input"
                    />
                  </IonItem>
                </IonCol>
              </IonRow>
              <IonRow>
                <IonCol size="12" className="ion-text-center">
                  <IonItem className="phone-input-item">
                    <IonLabel position="stacked" color="dark">Last Name</IonLabel>
                    <IonInput
                      type="text"
                      placeholder="Enter your last name"
                      value={lastName}
                      onIonInput={(e) => setLastName(e.detail.value)}
                      className="phone-input"
                    />
                  </IonItem>
                </IonCol>
              </IonRow>
              <IonRow>
                <IonCol size="12" className="ion-text-center">
                  <IonItem className="phone-input-item">
                    <IonLabel position="stacked" color="dark">Email Address</IonLabel>
                    <IonInput
                      type="email"
                      placeholder="Enter your email address"
                      value={email}
                      onIonInput={(e) => setEmail(e.detail.value)}
                      className="phone-input"
                    />
                  </IonItem>
                </IonCol>
              </IonRow>
              <IonRow>
                <IonCol size="12">
                  <IonButton
                    expand="block"
                    color="dark"
                    onClick={handleRegistration}
                    className="phone-modal-btn"
                  >
                    Complete Registration
                  </IonButton>
                </IonCol>
              </IonRow>
              <IonRow>
                <IonCol size="12">
                  <IonButton
                    expand="block"
                    fill="outline"
                    color="medium"
                    onClick={closeRegistrationModal}
                    className="phone-modal-btn"
                  >
                    Cancel
                  </IonButton>
                </IonCol>
              </IonRow>
            </IonGrid>
          </IonContent>
        </IonModal>

        {/* Terms and Conditions Modal */}
        <IonModal isOpen={termsModalOpen} onDidDismiss={handleTermsModalDismissed} className="terms-modal">
          <IonHeader className="terms-modal-header">
            <IonToolbar color="dark">
              <IonTitle>Terms and Conditions</IonTitle>
            </IonToolbar>
          </IonHeader>
          <IonContent className="terms-modal-content" scrollY={true}>
            <IonGrid>
              <IonRow>
                <IonCol size="12">
                  <div className="terms-text-container">
                    <pre className="terms-text">{termsContent}</pre>
                  </div>
                </IonCol>
              </IonRow>
              <IonRow>
                <IonCol size="12" className="ion-text-center">
                  <IonText color="dark">
                    <p style={{ fontWeight: 'bold', marginTop: '20px' }}>
                      By clicking "I Accept", you acknowledge that you have read and agree to these Terms and Conditions.
                    </p>
                  </IonText>
                </IonCol>
              </IonRow>
              <IonRow>
                <IonCol size="12">
                  <IonButton
                    expand="block"
                    color="dark"
                    onClick={handleAcceptTerms}
                    className="terms-modal-btn"
                  >
                    I Accept
                  </IonButton>
                </IonCol>
              </IonRow>
              <IonRow>
                <IonCol size="12">
                  <IonButton
                    expand="block"
                    fill="outline"
                    color="medium"
                    onClick={closeTermsModal}
                    className="terms-modal-btn"
                  >
                    Decline
                  </IonButton>
                </IonCol>
              </IonRow>
            </IonGrid>
          </IonContent>
        </IonModal>

        {/* Error Toast */}
        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={3000}
          position="top"
          color={toastColor}
        />

        {/* Loading Screen */}
        {isLoading && <LoadingScreen />}
      </IonContent>
    </IonPage>
  );
};

export default Landing;